import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardText,
  Lightning,
  FloppyDisk,
  FilePdf,
  TrashSimple,
  Plus,
  WarningCircle,
  CheckCircle,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Check, CaretUpDown } from "@phosphor-icons/react";
import {
  fetchTrucks,
  fetchAssignments,
  autoAssign,
  saveAssignments,
  fetchDriverHabits,
  api,
} from "@/lib/api";
import { useSites } from "@/lib/sites";

const RENTAL_LABEL = {
  own: "NY TRANS",
  "loc-eco": "Loc Eco",
  europcar: "Europcar",
  hertz: "Hertz",
  ada: "ADA",
  loa: "LOA",
  autre: "Autre",
};

function ownerLabelFor(truck, ownerMap = {}) {
  if (!truck) return "";
  const rt = truck.rental_type;
  if (!rt) return "";
  if (rt === "autre" && truck.rental_type_custom) return truck.rental_type_custom;
  return ownerMap[rt] || RENTAL_LABEL[rt] || rt;
}

function todayISO() {
  const d = new Date();
  const p = (n) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function frenchDate(iso) {
  try {
    const [y, m, d] = iso.split("-");
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return dt.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function AttributionPage() {
  const { sites: sitesList } = useSites();
  const [date, setDate] = useState(todayISO());
  const [siteFilter, setSiteFilter] = useState("DLP4");
  const [pasted, setPasted] = useState("");
  const [trucks, setTrucks] = useState([]);
  const [rows, setRows] = useState([]); // [{driver_name, truck_id, truck_plate, truck_brand, truck_site, reason, note}]
  const [meta, setMeta] = useState(null); // { unassigned_count, total_drivers, total_trucks_active, created_drivers }
  const [habits, setHabits] = useState({}); // { driver_name: { favorite_truck_id, favorite_plate, count, total_days } }
  const [ownersList, setOwnersList] = useState([]); // [{ key, label }]

  const ownerMap = useMemo(() => {
    const m = {};
    for (const o of ownersList) m[o.key] = o.label;
    return m;
  }, [ownersList]);

  useEffect(() => {
    api
      .get("/owners")
      .then((r) => setOwnersList(r.data || []))
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileInputRef = useRef(null);

  // Warn before browser-level navigation (refresh, close tab, back-button) when there are unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Intercept clicks on any internal navigation link (e.g. header NavLinks) while dirty
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      // Find the closest anchor that performs an internal navigation
      const anchor = e.target.closest && e.target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      // Same-page anchor (#xxx) → ignore
      if (href.startsWith("#")) return;
      // Same URL? (already on this page) → ignore
      if (href === window.location.pathname) return;
      // Ask the user
      const ok = window.confirm(
        "⚠ Tu as des modifications non sauvegardées dans l'attribution.\n\nQuitter cette page sans sauvegarder ?"
      );
      if (!ok) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [dirty]);

  const trucksById = useMemo(() => {
    const m = {};
    for (const t of trucks) m[t.id] = t;
    return m;
  }, [trucks]);

  const activeTrucks = useMemo(
    () => trucks.filter((t) => t.status === "actif" && !t.red_tag && t.site === siteFilter),
    [trucks, siteFilter]
  );

  // Selectable in manual dropdown: anything except 'inactif' AND 'rendu'
  // (a returned truck is no longer in the fleet — no point assigning it).
  const selectableTrucks = useMemo(
    () => trucks.filter((t) => t.status !== "inactif" && t.status !== "rendu" && t.site === siteFilter),
    [trucks, siteFilter]
  );

  // Full map of ALL trucks (all statuses & sites) — used to look up historical
  // assignments where the truck is no longer in `selectableTrucks` (panne/inactif/rendu)
  const truckById = useMemo(() => {
    const m = {};
    for (const t of trucks) m[t.id] = t;
    return m;
  }, [trucks]);

  const usedTruckIds = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.truck_id) s.add(r.truck_id);
    return s;
  }, [rows]);

  // Map: truck_id -> driver_name currently using it (for "occupé par X" hint in dropdown)
  const usedByDriver = useMemo(() => {
    const m = {};
    for (const r of rows) if (r.truck_id && r.driver_name) m[r.truck_id] = r.driver_name;
    return m;
  }, [rows]);

  // Load trucks + existing assignments for selected (date, site) on mount/change
  useEffect(() => {
    const load = async () => {
      try {
        const [tks, existing, habitsRes] = await Promise.all([
          fetchTrucks(),
          fetchAssignments(date, siteFilter),
          fetchDriverHabits(date).catch(() => ({ habits: {} })),
        ]);
        setTrucks(tks);
        setHabits(habitsRes?.habits || {});
        if (existing && existing.length > 0) {
          // Hydrate rows from saved assignments
          const tById = {};
          for (const t of tks) tById[t.id] = t;
          setRows(
            existing.map((a, idx) => ({
              uid: a.id || `saved-${idx}`,
              driver_name: a.driver_name,
              truck_id: a.truck_id || null,
              truck_plate: a.truck_plate || (a.truck_id ? tById[a.truck_id]?.license_plate || "" : ""),
              truck_brand: a.truck_id ? tById[a.truck_id]?.brand || "" : "",
              truck_site: a.truck_id ? tById[a.truck_id]?.site || "" : "",
              reason: a.truck_id ? "sauvegardé" : "non attribué",
              note: a.note || "",
              discord_validated: !!a.discord_validated,
            }))
          );
          setMeta({
            unassigned_count: existing.filter((a) => !a.truck_id).length,
            total_drivers: existing.length,
            total_trucks_active: tks.filter((t) => t.status === "actif" && !t.red_tag && t.site === siteFilter).length,
            loaded_from_saved: true,
          });
          setDirty(false);
        } else {
          setRows([]);
          setMeta(null);
        }
      } catch {
        toast.error("Erreur de chargement");
      }
    };
    load();
  }, [date, siteFilter]);

  const parseDrivers = (text) =>
    (text || "")
      .split(/[\n\r]+/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

  const doAutoAssign = async () => {
    const names = parseDrivers(pasted);
    if (names.length === 0) {
      toast.error("Colle d'abord la liste des chauffeurs");
      return;
    }
    setLoading(true);
    try {
      const res = await autoAssign(date, names, siteFilter);
      // Sort assignments alphabetically by driver name (French collation, case-insensitive)
      const sortedAssignments = [...res.assignments].sort((a, b) =>
        (a.driver_name || "").localeCompare(b.driver_name || "", "fr", {
          sensitivity: "base",
        })
      );
      setRows(
        sortedAssignments.map((a, idx) => ({
          uid: `auto-${Date.now()}-${idx}`,
          driver_name: a.driver_name,
          truck_id: a.truck_id,
          truck_plate: a.truck_plate,
          truck_brand: a.truck_brand,
          truck_site: a.truck_site,
          reason: a.reason,
          note: "",
          discord_validated: false,
        }))
      );
      setMeta({
        unassigned_count: res.unassigned_count,
        total_drivers: res.total_drivers,
        total_trucks_active: res.total_trucks_active,
        created_drivers: res.created_drivers || [],
      });
      setDirty(true);
      if (res.created_drivers && res.created_drivers.length > 0) {
        toast.success(
          `${res.assignments.length} chauffeurs · ${res.unassigned_count} non assigné(s) · ${res.created_drivers.length} créé(s)`
        );
      } else {
        toast.success(`${res.assignments.length} chauffeurs · ${res.unassigned_count} non assigné(s)`);
      }
    } catch (e) {
      toast.error("Erreur d'attribution automatique");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
    setDirty(true);
  };

  const changeTruck = (idx, truckId) => {
    if (truckId === "__none__") {
      updateRow(idx, {
        truck_id: null,
        truck_plate: "",
        truck_brand: "",
        truck_site: "",
        reason: "manuel · non attribué",
      });
      return;
    }
    const t = trucksById[truckId];
    if (!t) return;
    // If another row uses this truck, swap (move that row's truck to this one's previous)
    setRows((prev) => {
      const next = [...prev];
      const previousId = next[idx].truck_id;
      const conflictIdx = next.findIndex(
        (r, i) => i !== idx && r.truck_id === truckId
      );
      next[idx] = {
        ...next[idx],
        truck_id: t.id,
        truck_plate: t.license_plate,
        truck_brand: t.brand,
        truck_site: t.site,
        reason: "manuel",
      };
      if (conflictIdx >= 0) {
        const conflictPrev = next[conflictIdx];
        if (previousId) {
          const t2 = trucksById[previousId];
          next[conflictIdx] = {
            ...conflictPrev,
            truck_id: t2?.id || null,
            truck_plate: t2?.license_plate || "",
            truck_brand: t2?.brand || "",
            truck_site: t2?.site || "",
            reason: "permuté",
          };
        } else {
          next[conflictIdx] = {
            ...conflictPrev,
            truck_id: null,
            truck_plate: "",
            truck_brand: "",
            truck_site: "",
            reason: "permuté · non attribué",
          };
        }
      }
      return next;
    });
    setDirty(true);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addEmptyRow = () => {
    setRows((prev) => [
      ...prev,
      {
        uid: `manual-${Date.now()}-${prev.length}`,
        driver_name: "",
        truck_id: null,
        truck_plate: "",
        truck_brand: "",
        truck_site: "",
        reason: "ajouté manuellement",
        note: "",
      },
    ]);
    setDirty(true);
  };

  const moveRow = (idx, dir) => {
    setRows((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    const filtered = rows.filter((r) => (r.driver_name || "").trim().length > 0);
    if (filtered.length === 0) {
      toast.error("Aucune attribution à sauvegarder");
      return;
    }
    setSaving(true);
    try {
      const res = await saveAssignments(
        date,
        filtered.map((r, i) => ({
          driver_name: r.driver_name,
          truck_id: r.truck_id || null,
          note: r.note || "",
          order: i,
          discord_validated: !!r.discord_validated,
        })),
        siteFilter
      );
      toast.success(`Sauvegardé · ${res.saved} attribution(s)`);
      setDirty(false);
      if (siteFilter === "DLP4") {
        const fresh = await fetchAssignments(date, siteFilter);
        const noteOf = Object.fromEntries(fresh.map((a) => [a.driver_name, a.note || ""]));
        setRows((prev) => prev.map((r) => (r.driver_name in noteOf ? { ...r, note: noteOf[r.driver_name] } : r)));
      }
    } catch {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const exportPDF = () => {
    const filtered = rows.filter((r) => (r.driver_name || "").trim().length > 0);
    if (filtered.length === 0) {
      toast.error("Rien à exporter");
      return;
    }
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();

    // Header bar (black)
    doc.setFillColor(15, 15, 15);
    doc.rect(0, 0, pageW, 22, "F");

    // Gold accent bar
    doc.setFillColor(212, 175, 55);
    doc.rect(0, 22, pageW, 1.5, "F");

    // Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("NY TRANS", 14, 10);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(212, 175, 55);
    doc.text("ATTRIBUTION JOURNALIÈRE DES VÉHICULES", 14, 16);

    // Date (right)
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(frenchDate(date).toUpperCase(), pageW - 14, 12, { align: "right" });

    // Sub info
    const assignedCount = filtered.filter((r) => r.truck_id).length;
    const unassignedCount = filtered.length - assignedCount;
    const siteObj = sitesList.find((x) => x.code === siteFilter);
    const siteLabel = siteObj
      ? (siteObj.name ? `${siteObj.code} · ${siteObj.name.toUpperCase()}` : siteObj.code)
      : (siteFilter || "TOUS SITES");
    doc.setTextColor(80, 80, 80);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `Site : ${siteLabel} · ${filtered.length} chauffeur(s) · ${assignedCount} attribué(s) · ${unassignedCount} sans camion`,
      14,
      32
    );

    // Table
    autoTable(doc, {
      startY: 38,
      head: [["#", "Chauffeur", "Plaque", "Marque", "Site", "Notes"]],
      body: filtered.map((r, i) => [
        String(i + 1),
        r.driver_name,
        r.truck_plate || "—",
        (r.truck_brand || "").toUpperCase(),
        (r.truck_site || "").toUpperCase(),
        r.note || "",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [15, 15, 15],
        textColor: [212, 175, 55],
        fontStyle: "bold",
        fontSize: 9,
        cellPadding: 2.5,
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 2.2,
        textColor: [25, 25, 25],
      },
      alternateRowStyles: { fillColor: [248, 246, 240] },
      columnStyles: {
        0: { cellWidth: 10, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 65 },
        2: { cellWidth: 28, fontStyle: "bold" },
        3: { cellWidth: 25 },
        4: { cellWidth: 18, halign: "center" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 2 && data.cell.raw === "—") {
          data.cell.styles.textColor = [200, 50, 50];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY || 50;
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.4);
    doc.line(14, finalY + 8, pageW - 14, finalY + 8);

    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.text(
      `Document généré le ${new Date().toLocaleString("fr-FR")} · NY Trans · Contrôle de Flotte`,
      14,
      finalY + 14
    );

    // Signatures
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(15, 15, 15);
    doc.text("Chef Dispatcher :", 14, finalY + 28);
    doc.text("Signature & Date :", pageW / 2, finalY + 28);
    doc.setDrawColor(15, 15, 15);
    doc.line(14, finalY + 38, pageW / 2 - 10, finalY + 38);
    doc.line(pageW / 2, finalY + 38, pageW - 14, finalY + 38);

    const fname = `attribution-${siteFilter}-${date}.pdf`;
    doc.save(fname);
    toast.success("PDF exporté");
  };

  const remainingActive = activeTrucks.filter((t) => !usedTruckIds.has(t.id));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid="attribution-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">NY Trans</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-1">
            <ClipboardText size={28} weight="bold" className="inline mr-2 -mt-1 text-[var(--ny-gold)]" />
            Attribution journalière
          </h1>
          <div className="ny-divider w-32 mt-2" />
          <p className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">
            Colle la liste des chauffeurs · Le système attribue automatiquement les camions actifs selon l&apos;historique
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="label-tag">Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => {
                if (dirty && !window.confirm("Modifications non sauvegardées. Changer de date les abandonnera. Continuer ?")) {
                  return;
                }
                setDate(e.target.value);
              }}
              className="rounded-none border-zinc-900 w-44"
              data-testid="attrib-date"
            />
          </div>
        </div>
      </div>

      {/* Site tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-zinc-900 pb-0" data-testid="attrib-site-tabs">
        <div className="label-tag mr-2 self-center">Site :</div>
        {[
          ...sitesList.map((s) => ({ key: s.code, label: s.name ? `${s.code} · ${s.name}` : s.code })),
        ].map((s) => {
          const active = siteFilter === s.key;
          const count = trucks.filter((t) => t.status === "actif" && !t.red_tag && t.site === s.key).length;
          return (
            <button
              key={s.key}
              onClick={() => {
                if (dirty && !window.confirm("Tu as des modifications non sauvegardées. Changer de site les abandonnera. Continuer ?")) return;
                setSiteFilter(s.key);
              }}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border border-b-0 inline-flex items-center gap-2 ${
                active
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
              }`}
              data-testid={`attrib-tab-${s.key}`}
            >
              {s.label}
              <span className={`mono px-1.5 py-0.5 text-[10px] ${active ? "bg-[var(--ny-gold)] text-zinc-900" : "bg-zinc-100 text-zinc-600"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Step 1: Paste list */}
      <div className="border border-zinc-900 bg-white mb-4">
        <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-900 text-white flex items-center justify-between">
          <div className="label-tag !text-[var(--ny-gold-soft)]">1 · Liste des chauffeurs (copier-coller depuis Excel)</div>
          <div className="text-[10px] mono uppercase tracking-widest opacity-80">
            {parseDrivers(pasted).length} ligne(s)
          </div>
        </div>
        <div className="p-3">
          <Textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            rows={8}
            placeholder={`Mouad El Farchakhi\nAyyoub Khaïroun\nBillel Djafri\n...`}
            className="rounded-none border-zinc-300 font-mono text-sm"
            data-testid="attrib-paste"
          />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={doAutoAssign}
              disabled={loading || parseDrivers(pasted).length === 0}
              className="px-4 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40 inline-flex items-center gap-2"
              data-testid="attrib-auto-btn"
            >
              <Lightning size={16} weight="bold" /> {loading ? "Attribution…" : "Auto-attribuer"}
            </button>
            <button
              onClick={() => setPasted("")}
              disabled={!pasted}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 disabled:opacity-40"
              data-testid="attrib-clear"
            >
              Effacer
            </button>
            <div className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
              {activeTrucks.length} camion(s) actif(s) · {remainingActive.length} disponible(s)
            </div>
          </div>
        </div>
      </div>

      {/* Summary banner */}
      {meta && (
        <div className="border border-zinc-900 bg-white mb-4 px-3 py-2 flex flex-wrap items-center gap-3 text-xs" data-testid="attrib-meta">
          {meta.loaded_from_saved ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-900 font-bold uppercase tracking-widest text-[10px]">
              <CheckCircle size={12} weight="bold" /> Chargé depuis la sauvegarde
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-100 text-emerald-900 font-bold uppercase tracking-widest text-[10px]">
              <Lightning size={12} weight="bold" /> Proposition automatique
            </span>
          )}
          <span className="font-bold mono uppercase">
            {meta.total_drivers || rows.length} chauffeurs
          </span>
          <span className="text-zinc-500">·</span>
          <span className="font-bold mono uppercase">
            {rows.filter((r) => r.truck_id).length} attribués
          </span>
          {meta.unassigned_count > 0 && (
            <>
              <span className="text-zinc-500">·</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-red-100 text-red-800 font-bold uppercase tracking-widest text-[10px]">
                <WarningCircle size={12} weight="bold" /> {meta.unassigned_count} sans camion
              </span>
            </>
          )}
          {meta.created_drivers && meta.created_drivers.length > 0 && (
            <>
              <span className="text-zinc-500">·</span>
              <span className="text-amber-700 font-bold uppercase tracking-widest text-[10px]">
                {meta.created_drivers.length} nouveau(x) chauffeur(s) créé(s)
              </span>
            </>
          )}
          {dirty && (
            <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 text-amber-900 font-bold uppercase tracking-widest text-[10px]">
              ⚠ Modifications non sauvegardées
            </span>
          )}
        </div>
      )}

      {/* Step 2: Editable table */}
      {rows.length > 0 && (
        <div className="border border-zinc-900 bg-white mb-4">
          <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-900 text-white flex items-center justify-between gap-2 flex-wrap">
            <div className="label-tag !text-[var(--ny-gold-soft)]">2 · Vérifier & ajuster</div>
            <div className="flex items-center gap-2">
              <button
                onClick={addEmptyRow}
                className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/50 hover:bg-white hover:text-black inline-flex items-center gap-1"
                data-testid="attrib-add-row"
              >
                <Plus size={12} weight="bold" /> Ligne
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attrib-table">
              <thead className="bg-zinc-100 border-b border-zinc-900">
                <tr>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest font-bold w-10">#</th>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest font-bold">Chauffeur</th>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest font-bold">Camion</th>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest font-bold w-32">Source</th>
                  <th className="px-2 py-2 text-center text-[10px] uppercase tracking-widest font-bold w-20" title="Photo du camion postée sur Discord">Discord</th>
                  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest font-bold w-40">Note</th>
                  <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest font-bold w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const unassigned = !r.truck_id;
                  return (
                    <tr
                      key={r.uid || `row-${i}`}
                      className={`border-b border-zinc-200 ${unassigned ? "bg-red-50" : ""}`}
                      data-testid={`attrib-row-${i}`}
                    >
                      <td className="px-2 py-1.5 mono text-xs text-zinc-500">{i + 1}</td>
                      <td className="px-2 py-1.5">
                        <Input
                          value={r.driver_name}
                          onChange={(e) => updateRow(i, { driver_name: e.target.value })}
                          className="rounded-none border-zinc-300 h-8 text-sm font-bold"
                          data-testid={`attrib-driver-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <TruckCombobox
                          rowIndex={i}
                          value={r.truck_id}
                          trucks={selectableTrucks}
                          allTruckById={truckById}
                          rowTruckPlate={r.truck_plate}
                          usedTruckIds={usedTruckIds}
                          usedByDriver={usedByDriver}
                          ownerMap={ownerMap}
                          onChange={(tid) => changeTruck(i, tid)}
                        />
                        {(() => {
                          const h = habits[r.driver_name];
                          if (
                            !h ||
                            !h.favorite_truck_id ||
                            !r.truck_id ||
                            h.favorite_truck_id === r.truck_id
                          ) {
                            return null;
                          }
                          return (
                            <button
                              type="button"
                              onClick={() =>
                                changeTruck(i, h.favorite_truck_id)
                              }
                              className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-amber-100 border border-amber-500 text-amber-800 hover:bg-amber-200"
                              title={`Chauffeur habituellement sur ${h.favorite_plate} (${h.count}/${h.total_days} jours) — clic pour restaurer`}
                              data-testid={`attrib-habit-warn-${i}`}
                            >
                              ⚠ Habituel : {h.favorite_plate}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mono">
                        {r.reason}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer" title="Photo postée sur Discord ?">
                          <input
                            type="checkbox"
                            checked={!!r.discord_validated}
                            onChange={(e) => updateRow(i, { discord_validated: e.target.checked })}
                            className="w-4 h-4 accent-emerald-600"
                            data-testid={`attrib-discord-${i}`}
                          />
                        </label>
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          value={r.note || ""}
                          onChange={(e) => updateRow(i, { note: e.target.value })}
                          placeholder="Ex: cong/visite"
                          className="rounded-none border-zinc-300 h-8 text-xs"
                          data-testid={`attrib-note-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => moveRow(i, -1)}
                            className="px-1.5 py-1 text-[10px] font-bold border border-zinc-300 hover:border-zinc-900 disabled:opacity-30"
                            disabled={i === 0}
                            title="Monter"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveRow(i, +1)}
                            className="px-1.5 py-1 text-[10px] font-bold border border-zinc-300 hover:border-zinc-900 disabled:opacity-30"
                            disabled={i === rows.length - 1}
                            title="Descendre"
                          >
                            ↓
                          </button>
                          <button
                            onClick={() => removeRow(i)}
                            className="px-1.5 py-1 text-[10px] font-bold border border-zinc-300 hover:border-red-600 hover:text-red-600"
                            title="Retirer"
                            data-testid={`attrib-remove-${i}`}
                          >
                            <TrashSimple size={12} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 3: Save & Export */}
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 sticky bottom-2 bg-white border border-zinc-900 px-3 py-2 shadow-lg">
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="px-4 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="attrib-save"
          >
            <FloppyDisk size={16} weight="bold" /> {saving ? "Sauvegarde…" : dirty ? "Sauvegarder" : "Sauvegardé"}
          </button>
          <button
            onClick={exportPDF}
            className="px-4 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-[var(--ny-gold)] text-zinc-900 hover:bg-[var(--ny-gold-deep)] hover:text-white inline-flex items-center gap-2"
            data-testid="attrib-pdf"
          >
            <FilePdf size={16} weight="bold" /> Exporter PDF
          </button>
          {dirty && (
            <button
              onClick={doAutoAssign}
              disabled={loading || parseDrivers(pasted).length === 0}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 disabled:opacity-40 inline-flex items-center gap-2 ml-auto"
              data-testid="attrib-redo"
            >
              <ArrowsClockwise size={14} weight="bold" /> Refaire l&apos;auto-attribution
            </button>
          )}
        </div>
      )}

      {/* Hidden file input for future Excel import */}
      <input ref={fileInputRef} type="file" hidden accept=".xlsx,.csv,.txt" />
    </div>
  );
}

const STATUS_BADGES = {
  actif: { label: "ACTIF", cls: "bg-emerald-100 text-emerald-800" },
  panne: { label: "PANNE", cls: "bg-red-100 text-red-800" },
  rendu: { label: "RENDU", cls: "bg-zinc-200 text-zinc-700" },
  inactif: { label: "INACTIF", cls: "bg-zinc-200 text-zinc-500" },
  archive: { label: "ARCHIVÉ", cls: "bg-zinc-200 text-zinc-500" },
};

function TruckCombobox({
  rowIndex,
  value,
  trucks,
  allTruckById = {},
  rowTruckPlate = "",
  usedTruckIds,
  usedByDriver = {},
  ownerMap = {},
  onChange,
}) {
  const [open, setOpen] = useState(false);

  // The currently-selected truck may be missing from `trucks` (site mismatch OR
  // inactif/rendu → historical). Fallback to the full map + the plate stored on
  // the assignment row so we can still display it.
  const selected = useMemo(() => {
    if (!value) return null;
    return (
      trucks.find((t) => t.id === value) ||
      allTruckById[value] ||
      (rowTruckPlate ? { id: value, license_plate: rowTruckPlate, status: "archive" } : null)
    );
  }, [trucks, allTruckById, value, rowTruckPlate]);

  // Build the truck list to render, injecting the historical selection if not
  // already present in `trucks`.
  const displayTrucks = useMemo(() => {
    const list = [...trucks];
    if (selected && !list.some((t) => t.id === selected.id)) {
      list.unshift(selected);
    }
    return list;
  }, [trucks, selected]);

  // Split into 3 groups: libres | occupés | panne (+ historical / archived catch-all)
  const groups = useMemo(() => {
    const libres = [];
    const occupes = [];
    const pannes = [];
    const archived = [];
    for (const t of displayTrucks) {
      if (t.status === "panne") {
        pannes.push(t);
        continue;
      }
      if (t.status === "inactif" || t.status === "rendu" || t.status === "archive") {
        archived.push(t);
        continue;
      }
      const isUsedElsewhere = usedTruckIds.has(t.id) && t.id !== value;
      if (isUsedElsewhere) occupes.push(t);
      else libres.push(t);
    }
    // Deterministic alphabetic order inside each group
    const byPlate = (a, b) => (a.license_plate || "").localeCompare(b.license_plate || "");
    libres.sort(byPlate);
    occupes.sort(byPlate);
    pannes.sort(byPlate);
    archived.sort(byPlate);
    return { libres, occupes, pannes, archived };
  }, [displayTrucks, usedTruckIds, value]);

  const renderItem = (t) => {
    const usedElsewhere = usedTruckIds.has(t.id) && t.id !== value;
    const occupantName = usedElsewhere ? usedByDriver[t.id] : null;
    const status = STATUS_BADGES[t.status] || STATUS_BADGES.actif;
    const isSelected = t.id === value;
    const owner = ownerLabelFor(t, ownerMap);
    const searchValue = `${t.license_plate} ${t.brand || ""} ${t.model || ""} ${t.site || ""} ${t.status || ""} ${owner} ${occupantName || ""}`.toLowerCase();
    return (
      <CommandItem
        key={t.id}
        value={searchValue}
        onSelect={() => {
          onChange(t.id);
          setOpen(false);
        }}
        className={`rounded-none text-sm flex flex-col items-stretch gap-0.5 ${
          usedElsewhere ? "opacity-70" : ""
        }`}
        data-testid={`attrib-truck-option-${t.id}`}
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center min-w-0 flex-1">
            <Check
              size={12}
              weight="bold"
              className={`mr-2 shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`}
            />
            <span className="font-bold mono uppercase mr-2 shrink-0">
              {t.license_plate}
            </span>
            <span className="text-xs text-zinc-500 truncate">
              {(t.brand || "").toUpperCase()}
              {t.site ? ` · ${(t.site || "").toUpperCase()}` : ""}
              {owner && (
                <span className="ml-1 text-[10px] text-zinc-400 normal-case">
                  · {owner}
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {t.red_tag && (
              <span className="px-1 py-0.5 text-[9px] font-bold bg-red-600 text-white">
                RED
              </span>
            )}
            {t.status === "panne" && t.panne_severity && (
              <span
                className={`px-1 py-0.5 text-[9px] font-bold ${
                  t.panne_severity === "majeur"
                    ? "bg-red-600 text-white"
                    : "bg-amber-500 text-white"
                }`}
              >
                {t.panne_severity === "majeur" ? "MAJEURE" : "MINEURE"}
              </span>
            )}
            <span className={`px-1 py-0.5 text-[9px] font-bold uppercase ${status.cls}`}>
              {status.label}
            </span>
            {usedElsewhere && (
              <span className="px-1 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-800 uppercase">
                occupé
              </span>
            )}
          </div>
        </div>
        {occupantName && (
          <div
            className="pl-6 text-[10px] italic text-amber-700 truncate"
            data-testid={`attrib-truck-occupant-${t.id}`}
          >
            utilisé par <span className="font-bold not-italic">{occupantName}</span>
          </div>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 px-2 border border-zinc-300 bg-white text-sm flex items-center justify-between gap-1 hover:border-zinc-900 text-left"
          data-testid={`attrib-truck-${rowIndex}`}
        >
          <span className={`truncate ${selected ? "" : "text-zinc-400"}`}>
            {selected
              ? `${selected.license_plate}${
                  selected.brand ? ` · ${(selected.brand || "").toUpperCase()}` : ""
                }`
              : "—"}
          </span>
          <CaretUpDown size={12} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[360px]" align="start">
        <Command className="rounded-none">
          <CommandInput
            placeholder="Plaque, marque, chiffres…"
            className="text-sm"
            data-testid={`attrib-truck-search-${rowIndex}`}
          />
          <CommandList>
            <CommandEmpty>Aucun camion trouvé.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onChange("__none__");
                  setOpen(false);
                }}
                className="rounded-none text-xs uppercase tracking-widest text-zinc-500"
                data-testid={`attrib-truck-option-none-${rowIndex}`}
              >
                <Check
                  size={12}
                  weight="bold"
                  className={`mr-2 ${!value ? "opacity-100" : "opacity-0"}`}
                />
                — Aucun camion —
              </CommandItem>
            </CommandGroup>
            {groups.libres.length > 0 && (
              <CommandGroup heading={`Libres (${groups.libres.length})`}>
                {groups.libres.map(renderItem)}
              </CommandGroup>
            )}
            {groups.occupes.length > 0 && (
              <CommandGroup heading={`Occupés (${groups.occupes.length})`}>
                {groups.occupes.map(renderItem)}
              </CommandGroup>
            )}
            {groups.pannes.length > 0 && (
              <CommandGroup heading={`En panne (${groups.pannes.length})`}>
                {groups.pannes.map(renderItem)}
              </CommandGroup>
            )}
            {groups.archived.length > 0 && (
              <CommandGroup heading="⚑ Historique (hors flotte active)">
                {groups.archived.map(renderItem)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
